import { EOM_DATE_STR, YESTERDAY_DATE_RANGE } from "app/utils/date";

import { useValidatedCubeQuery } from "../useValidatedCubeQuery";
import { createCombinedErrorFromMultiple } from "../utils";
import {
  complianceStatsDistinctResourcesResultArraySchema,
  complianceStatsResourceCountResultArraySchema,
} from "./useComplianceStats.schema";

import type { BinaryOperator, Query } from "@cubejs-client/core";

interface UseComplianceStatsParams {
  dashboardKey: string;
  provider?: string | null;
  sectionKey?: string;
}

interface UseComplianceStatsResult {
  currentCompliancePercentage: number | null;
  previousCompliancePercentage: number | null;
  isLoading: boolean;
  error: Error | null;
}

function buildBaseQuery(
  measure: string,
  dimension: string,
  dateRange: string | [string, string],
  dashboardKey: string,
  provider: string | null | undefined,
  sectionKey?: string,
): Query {
  const filters = [
    {
      member: "Dashboard.key",
      operator: "equals" as BinaryOperator,
      values: [dashboardKey],
    },
    ...(sectionKey
      ? [
          {
            member: "DashboardSection.key",
            operator: "equals" as BinaryOperator,
            values: [sectionKey],
          },
        ]
      : []),
    ...(provider
      ? [
          {
            member: "Account.provider",
            operator: "equals" as BinaryOperator,
            values: [provider],
          },
        ]
      : []),
  ];

  return {
    measures: [measure],
    timeDimensions: [
      {
        dimension,
        granularity: "day",
        dateRange,
      },
    ],
    filters,
    dimensions: filters.map((filter) => filter.member),
  };
}

function buildResourceCountQuery(
  dateRange: string | [string, string],
  dashboardKey: string,
  provider: string | null | undefined,
  sectionKey?: string,
): Query {
  return buildBaseQuery(
    "ResourceCount.resourceCount",
    "ResourceCount.date",
    dateRange,
    dashboardKey,
    provider,
    sectionKey,
  );
}

function buildDistinctResourcesQuery(
  dateRange: string | [string, string],
  dashboardKey: string,
  provider: string | null | undefined,
  sectionKey?: string,
): Query {
  return buildBaseQuery(
    "ControlSignal.distinctResources",
    "ControlSignal.date",
    dateRange,
    dashboardKey,
    provider,
    sectionKey,
  );
}

export function useComplianceStats({
  dashboardKey,
  provider,
  sectionKey,
}: UseComplianceStatsParams): UseComplianceStatsResult {
  // We use "Yesterday" here b/c we might not have ControlSignal data for today yet
  const currentDateRange = YESTERDAY_DATE_RANGE;
  const previousDateRange: [string, string] = [EOM_DATE_STR, EOM_DATE_STR];

  const currentResourceCountQuery = useValidatedCubeQuery(
    buildResourceCountQuery(
      currentDateRange,
      dashboardKey,
      provider,
      sectionKey,
    ),
    complianceStatsResourceCountResultArraySchema,
  );

  const currentDistinctResourcesQuery = useValidatedCubeQuery(
    buildDistinctResourcesQuery(
      currentDateRange,
      dashboardKey,
      provider,
      sectionKey,
    ),
    complianceStatsDistinctResourcesResultArraySchema,
  );

  const previousResourceCountQuery = useValidatedCubeQuery(
    buildResourceCountQuery(
      previousDateRange,
      dashboardKey,
      provider,
      sectionKey,
    ),
    complianceStatsResourceCountResultArraySchema,
  );

  const previousDistinctResourcesQuery = useValidatedCubeQuery(
    buildDistinctResourcesQuery(
      previousDateRange,
      dashboardKey,
      provider,
      sectionKey,
    ),
    complianceStatsDistinctResourcesResultArraySchema,
  );

  const currentResourceCountResults =
    currentResourceCountQuery.resultSet?.tablePivot()[0] ?? {};
  const currentTotalResources = currentResourceCountResults[
    "ResourceCount.resourceCount"
  ]
    ? Number(currentResourceCountResults["ResourceCount.resourceCount"])
    : 0;

  const currentDistinctResourcesResults =
    currentDistinctResourcesQuery.resultSet?.tablePivot()[0] ?? {};
  const currentMatchedResources = currentDistinctResourcesResults[
    "ControlSignal.distinctResources"
  ]
    ? Number(currentDistinctResourcesResults["ControlSignal.distinctResources"])
    : 0;

  // Return null if no data exists (both zero) or if we'd have division by zero
  const currentCompliancePercentage =
    currentTotalResources === 0 && currentMatchedResources === 0
      ? null // No data case
      : currentTotalResources === 0
        ? null // Data inconsistency: can't have matched resources without total resources
        : Math.round(
            (100 * (currentTotalResources - currentMatchedResources)) /
              currentTotalResources,
          );

  const previousResourceCountResults =
    previousResourceCountQuery.resultSet?.tablePivot()[0] ?? {};
  const previousTotalResources = previousResourceCountResults[
    "ResourceCount.resourceCount"
  ]
    ? Number(previousResourceCountResults["ResourceCount.resourceCount"])
    : 0;

  const previousDistinctResourcesResults =
    previousDistinctResourcesQuery.resultSet?.tablePivot()[0] ?? {};
  const previousMatchedResources = previousDistinctResourcesResults[
    "ControlSignal.distinctResources"
  ]
    ? Number(
        previousDistinctResourcesResults["ControlSignal.distinctResources"],
      )
    : 0;

  // Return null if no data exists (both zero) or if we'd have division by zero
  const previousCompliancePercentage =
    previousTotalResources === 0 && previousMatchedResources === 0
      ? null // No data case
      : previousTotalResources === 0
        ? null // Data inconsistency: can't have matched resources without total resources
        : Math.round(
            (100 * (previousTotalResources - previousMatchedResources)) /
              previousTotalResources,
          );

  const isLoading =
    currentResourceCountQuery.isLoading ||
    currentDistinctResourcesQuery.isLoading ||
    previousResourceCountQuery.isLoading ||
    previousDistinctResourcesQuery.isLoading;

  const error = createCombinedErrorFromMultiple([
    {
      error: currentResourceCountQuery.error,
      validationError: currentResourceCountQuery.validationError,
      errorPrefix: "Current resource count validation failed",
    },
    {
      error: currentDistinctResourcesQuery.error,
      validationError: currentDistinctResourcesQuery.validationError,
      errorPrefix: "Current distinct resources validation failed",
    },
    {
      error: previousResourceCountQuery.error,
      validationError: previousResourceCountQuery.validationError,
      errorPrefix: "Previous resource count validation failed",
    },
    {
      error: previousDistinctResourcesQuery.error,
      validationError: previousDistinctResourcesQuery.validationError,
      errorPrefix: "Previous distinct resources validation failed",
    },
  ]);

  return {
    currentCompliancePercentage,
    previousCompliancePercentage,
    isLoading,
    error,
  };
}
